import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

// Map Font Awesome icon names to emojis
var iconToEmoji = {
  'utensils': '🍽️',
  'car': '🚗',
  'map': '🗺️',
  'hiking': '🏃',
  'shopping-bag': '🛍️',
  'spa': '💆',
  'moon': '🌙',
  'concierge-bell': '🛎️',
  'food': '🍽️',
  'transport': '🚗',
  'day-trips': '🗺️',
  'activities': '🏃',
  'shopping': '🛍️',
  'wellness': '💆',
  'nightlife': '🌙',
  'services': '🛎️'
};

function PropertyPortal() {
  var params = useParams();
  var unitCode = params.unitCode;
  var navigate = useNavigate();
  var i18n = useI18n();
  var t = i18n.t;
  var locale = i18n.locale;
  var setLocale = i18n.setLocale;
  var availableLocales = i18n.availableLocales;

  var propertyState = useState(null);
  var property = propertyState[0];
  var setProperty = propertyState[1];

  var offersState = useState([]);
  var allOffers = offersState[0];
  var setAllOffers = offersState[1];

  var categoriesState = useState([]);
  var categories = categoriesState[0];
  var setCategories = categoriesState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var categoryState = useState(null);
  var selectedCategory = categoryState[0];
  var setSelectedCategory = categoryState[1];

  var travelState = useState('walk');
  var travelMode = travelState[0];
  var setTravelMode = travelState[1];

  var infoState = useState(false);
  var showInfo = infoState[0];
  var setShowInfo = infoState[1];

  var offersRef = useRef(null);

  useEffect(function() {
    var saved = localStorage.getItem('selectedCategory_' + unitCode);
    if (saved && saved !== 'all') {
      setSelectedCategory(saved);
    }
  }, [unitCode]);

  useEffect(function() {
    localStorage.setItem('selectedCategory_' + unitCode, selectedCategory || 'all');
  }, [selectedCategory, unitCode]);

  useEffect(function() {
    loadProperty();
    loadCategories();

    function loadProperty() {
      api.get('/guest/property/' + unitCode + '?locale=' + locale)
        .then(function(data) {
          setProperty(data);
          setLoading(false);
        })
        .catch(function(err) {
          setError(err.message);
          setLoading(false);
        });
    }

    function loadCategories() {
      api.get('/guest/categories')
        .then(function(data) {
          setCategories(data);
        })
        .catch(function(err) {
          console.error('Failed to load categories:', err);
        });
    }
  }, [unitCode, locale]);

  useEffect(function() {
    if (property) {
      api.get('/guest/property/' + unitCode + '/offers?travel_mode=' + travelMode + '&locale=' + locale)
        .then(function(data) {
          setAllOffers(data);
        })
        .catch(function(err) {
          console.error('Failed to load offers:', err);
        });
    }
  }, [property, travelMode, locale, unitCode]);

  var offers = selectedCategory
    ? allOffers.filter(function(o) { return o.category_slug === selectedCategory; })
    : allOffers;

  function handleCategorySelect(slug) {
    setSelectedCategory(slug);
    setTimeout(function() {
      if (offersRef.current) {
        offersRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }

  function handleClaimOffer(offer) {
    var claimData = { travel_mode: travelMode };
    
    if (offer.offer_source === "owner_custom") {
      claimData.owner_offer_id = offer.offer_id;
      claimData.unit_code = unitCode;
    } else if (offer.property_offer_id) {
      claimData.property_offer_id = offer.property_offer_id;
    } else {
      // For default offers without property_offer_id, create a temporary link
      // Note: This might need special handling in production
      alert("This offer cannot be claimed directly. Contact the property owner.");
      return;
    }
    
    api.post("/guest/claim", claimData)
    .then(function(data) {
      navigate("/c/" + data.token);
    })
    .catch(function(err) {
      alert(err.message);
    });
  }

  function getCategoryName(slug, fallbackName) {
    var translated = t('categories.' + slug, { fallback: null });
    if (translated && translated.indexOf('categories.') !== 0) {
      return translated;
    }
    return fallbackName || slug;
  }

  function getIconEmoji(iconName) {
    return iconToEmoji[iconName] || '';
  }

  function formatWhatsAppUrl(number) {
    if (!number) return '';
    return 'https://wa.me/' + number.replace(/\D/g, '');
  }

  function getOfferImageUrl(offer) {
    if (offer.image_url) return offer.image_url;
    if (offer.category_default_image_url) return offer.category_default_image_url;
    return '/img/defaults/generic.svg';
  }

  if (loading) {
    return React.createElement('div', { className: 'container', style: { paddingTop: '40px' } },
      React.createElement('div', { className: 'flex justify-center' },
        React.createElement('div', { className: 'spinner' })
      )
    );
  }

  if (error) {
    return React.createElement('div', { className: 'container', style: { paddingTop: '40px', textAlign: 'center' } },
      React.createElement('h2', null, t('guest.propertyNotFound')),
      React.createElement('p', { className: 'text-secondary mt-2' }, error)
    );
  }

  var branding = property.branding;
  var info = property.info;
  var owner = property.owner;

  return React.createElement('div', { style: { minHeight: '100vh', background: 'var(--background)' } },
    // Header / Cover
    React.createElement('div', {
      style: {
        background: branding && branding.cover_image_url
          ? 'url(' + branding.cover_image_url + ') center/cover'
          : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
        height: '180px',
        position: 'relative'
      }
    },
      // Language switcher
      React.createElement('div', { style: { position: 'absolute', top: '12px', right: '12px' } },
        React.createElement('select', {
          value: locale,
          onChange: function(e) { setLocale(e.target.value); },
          style: {
            padding: '6px 10px',
            borderRadius: '6px',
            border: 'none',
            background: 'rgba(255,255,255,0.9)',
            fontSize: '14px'
          }
        },
          availableLocales.map(function(l) {
            return React.createElement('option', { key: l, value: l }, l.toUpperCase());
          })
        )
      )
    ),

    // Main content
    React.createElement('div', { className: 'container', style: { marginTop: '-60px', position: 'relative', zIndex: 1 } },
      // Property info card
      React.createElement('div', { className: 'card', style: { textAlign: 'center' } },
        branding && branding.logo_url && React.createElement('img', {
          src: branding.logo_url,
          alt: '',
          style: { width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', marginTop: '-50px', border: '3px solid white' }
        }),
        React.createElement('h1', { style: { fontSize: '22px', marginTop: branding && branding.logo_url ? '8px' : '0', fontWeight: '600' } },
          property.property.name
        ),
        branding && branding.tagline && React.createElement('p', { className: 'text-secondary', style: { fontSize: '15px' } }, branding.tagline),

        // Owner contact
        owner && React.createElement('div', { className: 'flex gap-2 justify-center mt-3', style: { flexWrap: 'wrap' } },
          owner.phone && React.createElement('a', { href: 'tel:' + owner.phone, className: 'btn btn-secondary', style: { padding: '8px 12px', fontSize: '14px' } }, t('guest.call')),
          owner.whatsapp_number && React.createElement('a', { href: formatWhatsAppUrl(owner.whatsapp_number), className: 'btn btn-secondary', style: { padding: '8px 12px', fontSize: '14px' } }, t('guest.whatsapp')),
          owner.line_id && React.createElement('a', { href: 'https://line.me/R/ti/p/' + owner.line_id, className: 'btn btn-secondary', style: { padding: '8px 12px', fontSize: '14px' } }, t('guest.line')),
          owner.website && React.createElement('a', { href: owner.website.startsWith('http') ? owner.website : 'https://' + owner.website, target: '_blank', rel: 'noopener noreferrer', className: 'btn btn-secondary', style: { padding: '8px 12px', fontSize: '14px' } }, t('guest.website') || 'Website'),
          owner.email && React.createElement('a', { href: 'mailto:' + owner.email, className: 'btn btn-secondary', style: { padding: '8px 12px', fontSize: '14px' } }, t('guest.email') || 'Email')
        ),

        // House info toggle
        info && (info.wifi_ssid || info.house_rules || info.checkin_info) && React.createElement('button', {
          onClick: function() { setShowInfo(!showInfo); },
          className: 'btn btn-secondary btn-block mt-3',
          style: { fontSize: '14px' }
        }, showInfo ? t('guest.hide') : t('guest.show'), ' ', t('guest.houseInfo')),

        // House info panel
        showInfo && info && React.createElement('div', { style: { textAlign: 'left', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)' } },
          info.wifi_ssid && React.createElement('div', { className: 'mb-3' },
            React.createElement('div', { className: 'text-secondary', style: { fontSize: '13px' } }, t('guest.wifi')),
            React.createElement('div', { style: { fontSize: '15px' } }, React.createElement('strong', null, t('guest.wifiName'), ':'), ' ', info.wifi_ssid),
            info.wifi_password && React.createElement('div', { style: { fontSize: '15px' } }, React.createElement('strong', null, t('guest.wifiPassword'), ':'), ' ', info.wifi_password)
          ),
          info.checkin_info && React.createElement('div', { className: 'mb-3' },
            React.createElement('div', { className: 'text-secondary', style: { fontSize: '13px' } }, t('guest.checkIn')),
            React.createElement('div', { style: { whiteSpace: 'pre-wrap', fontSize: '15px' } }, info.checkin_info)
          ),
          info.checkout_info && React.createElement('div', { className: 'mb-3' },
            React.createElement('div', { className: 'text-secondary', style: { fontSize: '13px' } }, t('guest.checkOut')),
            React.createElement('div', { style: { whiteSpace: 'pre-wrap', fontSize: '15px' } }, info.checkout_info)
          ),
          info.house_rules && React.createElement('div', { className: 'mb-3' },
            React.createElement('div', { className: 'text-secondary', style: { fontSize: '13px' } }, t('guest.houseRules')),
            React.createElement('div', { style: { whiteSpace: 'pre-wrap', fontSize: '15px' } }, info.house_rules)
          ),
          info.emergency_info && React.createElement('div', { className: 'mb-3' },
            React.createElement('div', { className: 'text-secondary', style: { fontSize: '13px' } }, t('guest.emergency')),
            React.createElement('div', { style: { whiteSpace: 'pre-wrap', fontSize: '15px' } }, info.emergency_info)
          )
        )
      ),

      // Offers section header
      React.createElement('h2', { style: { fontSize: '18px', marginBottom: '12px', fontWeight: '600' } }, t('guest.offers')),

      // Sticky Filter Bar
      React.createElement('div', { className: 'sticky-filter-bar', ref: offersRef },
        // Category Chips
        React.createElement('div', { className: 'category-chip-bar' },
          React.createElement('div', { className: 'category-chips' },
            React.createElement('button', {
              className: 'category-chip' + (!selectedCategory ? ' active' : ''),
              onClick: function() { handleCategorySelect(null); }
            }, t('common.all')),
            categories.map(function(cat) {
              return React.createElement('button', {
                key: cat.id,
                className: 'category-chip' + (selectedCategory === cat.slug ? ' active' : ''),
                onClick: function() { handleCategorySelect(cat.slug); }
              }, getIconEmoji(cat.icon), ' ', getCategoryName(cat.slug, cat.name));
            })
          )
        ),
        // Travel mode toggle
        React.createElement('div', { className: 'toggle-group' },
          React.createElement('button', {
            className: 'toggle-btn' + (travelMode === 'walk' ? ' active' : ''),
            onClick: function() { setTravelMode('walk'); }
          }, '🚶 ', t('guest.walking')),
          React.createElement('button', {
            className: 'toggle-btn' + (travelMode === 'drive' ? ' active' : ''),
            onClick: function() { setTravelMode('drive'); }
          }, '🚗 ', t('guest.driving'))
        )
      ),

      // Offers list
      offers.length === 0
        ? React.createElement('div', { className: 'card text-center text-secondary' }, t('guest.noOffers'))
        : offers.map(function(offer) {
            return React.createElement('div', { key: offer.property_offer_id || offer.offer_id || Math.random(), className: 'guest-offer-card' },
              React.createElement('div', { className: 'offer-card-content' },
                // Thumbnail
                React.createElement('div', { className: 'offer-thumbnail' },
                  React.createElement('img', {
                    src: getOfferImageUrl(offer),
                    alt: offer.title,
                    loading: 'lazy',
                    onError: function(e) { e.target.src = '/img/defaults/generic.svg'; }
                  })
                ),
                // Details
                React.createElement('div', { className: 'offer-details' },
                  React.createElement('div', { className: 'flex justify-between items-center mb-1' },
                    !selectedCategory && React.createElement('span', { className: 'badge badge-category' },
                      getCategoryName(offer.category_slug, offer.category_name)
                    ),
                    offer.listing_level === 'featured' || offer.is_featured && React.createElement('span', { className: 'badge badge-featured' }, t('guest.featured'))
                  ),
                  React.createElement('h3', null, offer.title),
                  React.createElement('p', { className: 'vendor-name' }, offer.vendor_name),
                  offer.short_description && React.createElement('p', { className: 'description' }, offer.short_description),
                  React.createElement('div', { className: 'flex justify-between items-center', style: { marginTop: '8px' } },
                    React.createElement('span', { className: 'distance' },
                      travelMode === 'walk'
                        ? (offer.walk_time_min ? t('guest.minWalk', { time: offer.walk_time_min }) : '')
                        : (offer.drive_time_min ? t('guest.minDrive', { time: offer.drive_time_min }) : '')
                    ),
                    React.createElement('button', {
                      className: 'btn btn-primary',
                      style: { padding: '8px 16px', fontSize: '14px' },
                      onClick: function() { handleClaimOffer(offer); }
                    }, t('guest.claimOffer'))
                  )
                )
              )
            );
          }),

      // Add to home screen hint
      React.createElement('div', { className: 'card text-center text-secondary', style: { marginBottom: '32px', fontSize: '14px' } },
        t('guest.addToHomeScreen')
      )
    )
  );
}

export default PropertyPortal;
